import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { CSSProperties } from 'react';
import { useCallback } from 'react';
import { useState } from 'react';
import { KeyFactorsImpactGrade } from 'apps/sigma2-ui/src/components/key-factors/types';
import { KeyFactorsGradedImpactDataPoint } from 'apps/sigma2-ui/src/components/key-factors/types';
import { CardMeasurement } from 'apps/sigma2-ui/src/components/key-factors/radial-impact-chart/RadialImpactChartCalloutCard';

type RingDimensionStyle = Pick<CSSProperties, 'left' | 'top' | 'height'>;

export const getRingRadius = ({
  ringIndex,
  totalRings,
  parentHeight,
}: {
  ringIndex: number;
  totalRings: number;
  parentHeight: number;
}) => {
  const maxSize = 100;
  const minSize = 20; // Accounts for the inner circle.
  const usableRange = maxSize - minSize;
  const step = usableRange / totalRings;
  const height = maxSize - ringIndex * step;
  const pixelHeight = (height / 100) * parentHeight;

  return pixelHeight / 2;
};

export const getRingDimensionStyle = ({
  index,
  totalRings,
  parentHeight,
}: {
  index: number;
  totalRings: number;
  parentHeight: number;
}): RingDimensionStyle => {
  const pixelHeight =
    getRingRadius({ ringIndex: index, totalRings, parentHeight }) * 2;
  const height = (pixelHeight / parentHeight) * 100;
  const offset = (100 - height) / 2;

  return {
    height: `${pixelHeight}px`,
    top: `${offset}%`,
    left: `${offset}%`,
  };
};

const ringBackgroundGradient = (
  grade: KeyFactorsImpactGrade,
  isHovered: boolean,
): CSSProperties['background'] => {
  switch (grade) {
    case 'high':
      return `radial-gradient(50% 50% at 50% 50%, ${isHovered ? '#3661E2' : '#537AEE'} 0%, ${isHovered ? '#A1B8F7' : '#DFE6F9'} 100%)`;
    case 'medium':
      return `radial-gradient(50% 50% at 50% 50%, ${isHovered ? '#933EE5' : '#A95FF1'} 0%, ${isHovered ? '#CFA9F5' : '#EFE2FD'} 100%)`;
    case 'low':
      return `radial-gradient(50% 50% at 50% 50%, ${isHovered ? '#696D70' : '#7F8488'} 0%, ${isHovered ? '#B8BABC' : '#E7E8E8'} 100%)`;
    default:
      throw guardExhaustive(grade);
  }
};

const ringStrokeColor = (grade: KeyFactorsImpactGrade, isHovered: boolean) => {
  if (!isHovered) {
    return '#FFF';
  }

  switch (grade) {
    case 'high':
      return '#1C47CA';
    case 'medium':
      return '#791FD1';
    case 'low':
      return '#52575B';
    default:
      throw guardExhaustive(grade);
  }
};

export const ringStyle = ({
  index,
  grade,
  parentHeight,
  totalRings,
  isHovered,
}: KeyFactorsGradedImpactDataPoint & {
  index: number;
  parentHeight: number;
  totalRings: number;
  isHovered: boolean;
}): CSSProperties => ({
  ...getRingDimensionStyle({ index, totalRings, parentHeight }),
  position: 'absolute',
  aspectRatio: '1 / 1',
  borderRadius: '50%',
  background: ringBackgroundGradient(grade, isHovered),
  border: `1px solid ${ringStrokeColor(grade, isHovered)}`,
  stroke: ringStrokeColor(grade, isHovered),
  filter: 'drop-shadow(0 0 20px rgba(83, 122, 238, 0.06))',
});

export const ringOutlineStyle = ({
  index,
  parentHeight,
  totalRings,
}: {
  index: number;
  parentHeight: number;
  totalRings: number;
}): CSSProperties => ({
  ...getRingDimensionStyle({ index, totalRings, parentHeight }),
  position: 'absolute',
  aspectRatio: '1 / 1',
  borderRadius: '50%',
  border: '1px solid var(--gray-150, #E7E8E8)',
});

export const useIndexedMeasurements = (count: number) => {
  const [measurements, setMeasurements] = useState<CardMeasurement[]>(() =>
    Array.from({ length: count }, () => ({
      width: 0,
      height: 0,
      top: 0,
    })),
  );
  const update = useCallback(
    (index: number, next: CardMeasurement) =>
      setMeasurements((prev) => {
        const cur = prev[index];
        if (
          cur &&
          cur.width === next.width &&
          cur.height === next.height &&
          cur.top === next.top
        ) {
          return prev;
        }

        const copy = prev.slice();
        copy[index] = next;
        return copy;
      }),
    [],
  );
  return [measurements, update] as const;
};

export const useIndexedHover = (count: number) => {
  const [isHovered, setIsHovered] = useState<boolean[]>(() =>
    Array(count).fill(false),
  );

  const update = useCallback(
    (index: number, next: boolean) =>
      setIsHovered((prev) => {
        const cur = prev[index];
        if (cur === next) return prev;

        if (next) {
          const newArr = Array(count).fill(false);
          newArr[index] = true;
          return newArr;
        }

        const copy = prev.slice();
        copy[index] = false;
        return copy;
      }),
    [count],
  );

  return [isHovered, update] as const;
};
